/**
 * Hook for accessing user settings from localStorage
 */

import { useState, useEffect, useCallback } from 'react';
import { logger } from '@/lib/services/logger';

export interface UserSettings {
  telegramNotifications: boolean;
  emailNotifications: boolean;
  timezone: string;
  aiProcessing: boolean;
  language: string;
  hideContacts: boolean;
}

const DEFAULT_SETTINGS: UserSettings = {
  telegramNotifications: true,
  emailNotifications: false,
  timezone: 'Europe/Moscow',
  aiProcessing: true,
  language: 'ru',
  hideContacts: true, // Default to true - hide contacts
};

const SETTINGS_KEY = 'userSettings';

export const useUserSettings = () => {
  const [settings, setSettings] = useState<UserSettings>(DEFAULT_SETTINGS);

  useEffect(() => {
    const savedSettings = localStorage.getItem(SETTINGS_KEY);
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setSettings(prev => ({ ...prev, ...parsed }));
      } catch (e) {
        logger.warn('Failed to parse saved settings', { error: e, context: { feature: 'settings' } });
      }
    }
  }, []);

  return settings;
};

/**
 * Check if AI processing is enabled in user settings
 */
export const isAIProcessingEnabled = (): boolean => {
  const savedSettings = localStorage.getItem(SETTINGS_KEY);
  if (savedSettings) {
    try {
      const parsed = JSON.parse(savedSettings);
      return parsed.aiProcessing !== false;
    } catch {
      return true;
    }
  }
  return true; // Default to enabled
};

/**
 * Check if hideContacts is enabled in user settings
 */
export const isHideContactsEnabled = (): boolean => {
  const savedSettings = localStorage.getItem(SETTINGS_KEY);
  if (savedSettings) {
    try {
      const parsed = JSON.parse(savedSettings);
      return parsed.hideContacts !== false;
    } catch {
      return true;
    }
  }
  return true; // Default to enabled
};

/**
 * Check if there's an ad draft in localStorage
 */
export const hasAdDraft = (): boolean => {
  try {
    return localStorage.getItem('drug_ad_draft') !== null;
  } catch {
    return false;
  }
};
